// Session locale de l'agent de terrain (D-029) : trente jours renouvelés à chaque synchronisation,
// code local à six chiffres demandé à chaque réouverture de l'application (empreinte SHA-256 salée,
// jamais le code lui-même). Les données d'un autre compte sont purgées avant l'ouverture.
import type { ContexteActif } from '../api/types';
import { nomCompte } from '../session/permissions';
import { sha256Hex } from './photos';
import { ecrireSessionLocale, effacerSessionLocale, lireSessionLocale, viderDonneesTravail, type SessionLocale } from './stockage';

export const DUREE_SESSION_LOCALE_MS = 30 * 86_400_000;
export const CLE_DEVERROUILLAGE = 'cc-terrain:deverrouille';

export function sessionLocaleValide(s: SessionLocale | null, maintenant = new Date()): s is SessionLocale {
  return Boolean(s && new Date(s.expire_le).getTime() > maintenant.getTime());
}

/**
 * Ouvre ou renouvelle la session locale du compte connecté. Un autre compte ou espace était enregistré :
 * ses missions, opérations et photos sont effacées (R14, aucune donnée croisée) et son code aussi.
 */
export async function ouvrirSessionLocale(contexte: ContexteActif, maintenant = new Date()): Promise<{ session: SessionLocale; compteChange: boolean }> {
  const existante = await lireSessionLocale();
  const compteChange = Boolean(existante && (existante.compte_id !== contexte.compte.id || existante.appartenance_id !== contexte.appartenance_active?.id));
  if (!existante || compteChange) {
    await viderDonneesTravail();
    await effacerSessionLocale();
    verrouiller();
  }
  const precedente = compteChange ? null : existante;
  const session: SessionLocale = {
    compte_id: contexte.compte.id,
    identifiant: contexte.compte.identifiant,
    nom: nomCompte(contexte),
    appartenance_id: contexte.appartenance_active?.id ?? '',
    collectivite: contexte.appartenances.find((a) => a.id === contexte.appartenance_active?.id)?.collectivite?.nom ?? null,
    ouverte_le: precedente?.ouverte_le ?? maintenant.toISOString(),
    renouvelee_le: maintenant.toISOString(),
    expire_le: new Date(maintenant.getTime() + DUREE_SESSION_LOCALE_MS).toISOString(),
    code: precedente?.code ?? null,
  };
  await ecrireSessionLocale(session);
  return { session, compteChange };
}

export async function renouvelerSessionLocale(maintenant = new Date()): Promise<SessionLocale | null> {
  const s = await lireSessionLocale();
  if (!s) return null;
  const r = { ...s, renouvelee_le: maintenant.toISOString(), expire_le: new Date(maintenant.getTime() + DUREE_SESSION_LOCALE_MS).toISOString() };
  await ecrireSessionLocale(r);
  return r;
}

export async function fermerSessionLocale(): Promise<void> {
  await effacerSessionLocale();
  verrouiller();
}

export function codeValide(code: string): boolean {
  return /^\d{6}$/.test(code);
}

function selAleatoire(): string {
  const o = new Uint8Array(16);
  crypto.getRandomValues(o);
  return Array.from(o, (b) => b.toString(16).padStart(2, '0')).join('');
}

async function empreinteCode(sel: string, code: string): Promise<string> {
  return sha256Hex(new TextEncoder().encode(`${sel}:${code}`));
}

export async function definirCodeLocal(code: string): Promise<void> {
  if (!codeValide(code)) throw new Error('Le code comporte six chiffres');
  const s = await lireSessionLocale();
  if (!s) throw new Error('Aucune session locale');
  const sel = selAleatoire();
  await ecrireSessionLocale({ ...s, code: { sel, empreinte: await empreinteCode(sel, code) } });
}

export async function verifierCodeLocal(code: string): Promise<boolean> {
  const s = await lireSessionLocale();
  if (!s?.code || !codeValide(code)) return false;
  return (await empreinteCode(s.code.sel, code)) === s.code.empreinte;
}

/** Déverrouillage valable pour l'onglet courant (sessionStorage) : perdu à la fermeture de l'application. */
export function estDeverrouille(compteId: string): boolean {
  try {
    return window.sessionStorage.getItem(CLE_DEVERROUILLAGE) === compteId;
  } catch {
    return false;
  }
}

export function deverrouiller(compteId: string): void {
  try {
    window.sessionStorage.setItem(CLE_DEVERROUILLAGE, compteId);
  } catch {
    // Stockage de session indisponible : le code sera redemandé à la prochaine page.
  }
}

export function verrouiller(): void {
  try {
    window.sessionStorage.removeItem(CLE_DEVERROUILLAGE);
  } catch {
    // Rien à faire.
  }
}
